
/**
 * QuarterlyUpdateTable Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive table component for displaying quarterly update data with sorting, search highlighting,
 * and PDF modal functionality. Uses react-table for advanced table features and provides interactive
 * access to quarterly update documents through integrated modal interfaces.
 * 
 * Features:
 * - Sortable table columns with visual indicators
 * - Search query highlighting in content
 * - Quarter-based color coding (Q1-Q4)
 * - Current quarter identification and highlighting
 * - PDF modal integration for document viewing
 * - Multiple modal management with z-index stacking
 * - Custom sorting for quarters and dates
 * - No-results state handling
 * - Responsive design with tooltips
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of quarterly update objects to display in table
 * @param {string} [props.searchQuery=''] - Search query string for highlighting matches
 * @returns {JSX.Element} The rendered quarterly update table component
 * 
 * @example
 * ```jsx
 * <QuarterlyUpdateTable 
 *   data={quarterlyData} 
 *   searchQuery="Q1" 
 * />
 * ```
 */

import { useMemo, useState } from 'react';
import {
  useTable,
  tableFeatures,
  rowSortingFeature,
  columnVisibilityFeature,
  createSortedRowModel,
  sortFn_alphanumeric,
  sortFn_text,
  flexRender,
} from '@tanstack/react-table';
import PDFModal from '../../modals/PDFModal';
import { formatDate, formatDateSimple } from '../../utils/textUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import './QuarterlyUpdateTable.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

/**
 * Custom sorting function for quarter column
 *
 * Implements descending sort order with year as primary sort and quarter as secondary.
 * Ensures most recent quarters appear at the top of the table when sorted.
 *
 * @param {Object} rowA - First row object for comparison
 * @param {Object} rowB - Second row object for comparison
 * @returns {number} Sort comparison result (-1, 0, 1)
 *
 * @example
 * ```javascript
 * // Sorts Q4 2024 before Q1 2024, Q4 2023 before Q3 2023, etc.
 * customSort(rowA, rowB) // Returns comparison value
 * ```
 */
const customSort = (rowA, rowB) => {
    const yearDiff = parseInt(rowB.original.Year, 10) - parseInt(rowA.original.Year, 10);
    if (yearDiff !== 0) return yearDiff;

    // Reverse the quarter map for descending order
    const quarterMap = { Q4: 1, Q3: 2, Q2: 3, Q1: 4 };
    return quarterMap[rowA.original.Quarter] - quarterMap[rowB.original.Quarter];
};

// Declared once at module scope so the table is not rebuilt on every render.
// Named sort functions are registered on the `sortFns` slot and referenced by
// key from a column's `sortFn` option. The built-ins must be registered too:
// v9 no longer bundles them, so auto-detected columns would otherwise fall back
// to a case-sensitive basic compare.
const features = tableFeatures({
    rowSortingFeature,
    columnVisibilityFeature,
    sortedRowModel: createSortedRowModel(),
    sortFns: {
        customSort,
        alphanumeric: sortFn_alphanumeric,
        text: sortFn_text,
    },
});

function QuarterlyUpdateTable({ data, searchQuery = '' }) {
    const [modals, setModals] = useState([]);

    /**
     * Get CSS color class for quarter
     * 
     * Maps quarter values (Q1-Q4) to predefined color classes for consistent
     * visual differentiation across quarters in the table display.
     * 
     * @param {string} quarter - The quarter string (Q1, Q2, Q3, Q4)
     * @returns {string} CSS class name for the quarter color
     * 
     * @example
     * ```javascript
     * getQuarterColor('Q1') // Returns 'q1'
     * getQuarterColor('Q4') // Returns 'q4'
     * getQuarterColor('unknown') // Returns 'q1' (default)
     * ```
     */
    const getQuarterColor = (quarter) => {
        const colorMap = {
            'Q1': 'q1',
            'Q2': 'q2',
            'Q3': 'q3',
            'Q4': 'q4'
        };
        return colorMap[quarter] || 'q1';
    };

    /**
     * Check if the given year and quarter represents the current quarter
     * 
     * Compares the provided year and quarter against the current date to determine
     * if this represents the current business quarter for highlighting purposes.
     * 
     * @param {string|number} year - The year to check
     * @param {string} quarter - The quarter to check (Q1, Q2, Q3, Q4)
     * @returns {boolean} True if this is the current quarter
     * 
     * @example
     * ```javascript
     * isCurrentQuarter('2024', 'Q1') // Returns true if current date is in Q1 2024
     * isCurrentQuarter('2023', 'Q4') // Returns false if not current quarter
     * ```
     */
    const isCurrentQuarter = (year, quarter) => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth(); // 0-based
        const currentQuarter = Math.floor(currentMonth / 3) + 1;
        return parseInt(year) === currentYear && quarter === `Q${currentQuarter}`;
    };

    /**
     * Open a PDF modal or bring existing modal to front
     * 
     * Manages multiple PDF modals with proper z-index stacking. If a modal for the same
     * quarterly update already exists, it brings it to the front. Otherwise, creates a new modal
     * with proper positioning and z-index management.
     * 
     * @param {string|number} id - Unique identifier for the quarterly update
     * @param {string} title - Title to display in the modal header
     * 
     * @example
     * ```javascript
     * openPDFModal('q1-2024', 'Q1 March 2024')
     * ```
     */
    const openPDFModal = (id, title) => {
        setModals(prevModals => {
            const existingModalIndex = prevModals.findIndex(modal => modal.id === id);
            const maxZIndex = Math.max(...prevModals.map(modal => modal.zIndex), 0) + 1;

            if (existingModalIndex !== -1) {
                const updatedModals = [...prevModals];
                updatedModals[existingModalIndex].isOpen = true;
                updatedModals[existingModalIndex].zIndex = maxZIndex;
                return updatedModals;
            } else {
                const newModal = {
                    id,
                    title,
                    isOpen: true,
                    zIndex: maxZIndex,
                };
                return [...prevModals, newModal];
            }
        });
    };

    /**
     * Close a PDF modal by ID
     * 
     * Marks the specified modal as closed while maintaining it in the modals array
     * for potential reopening. The modal component handles the actual visibility.
     * 
     * @param {string|number} id - Unique identifier of the modal to close
     * 
     * @example
     * ```javascript
     * closePDFModal('q1-2024')
     * ```
     */
    const closePDFModal = id => {
        setModals(prevModals => prevModals.map(modal => modal.id === id ? { ...modal, isOpen: false } : modal));
    };

    /**
     * Table column definitions using react-table format
     * 
     * Defines the structure and behavior of each table column including:
     * - Header text and custom sorting behavior
     * - Cell rendering with search highlighting
     * - Quarter and status tag integration
     * - PDF modal integration for clickable elements
     * - Custom data accessors and formatting
     * 
     * @type {Array} Column configuration array for react-table
     */
    const columns = useMemo(() => [
        {
            header: 'Quarter & Details',
            accessorKey: 'Quarter',
            cell: ({ row }) => (
                <div className="quarterly-table-content">
                    <h4 className="quarterly-table-title">
                        <a 
                            className='quarterly-table-link' 
                            onClick={e => {
                                e.stopPropagation();
                                openPDFModal(
                                    row.original.id,
                                    `${row.original.Quarter} ${row.original.Month} ${row.original.Year}`
                                );
                            }}
                        >
                            {searchQuery.trim() ? (
                                <span dangerouslySetInnerHTML={{ 
                                    __html: `${row.original.Quarter} ${row.original.Month} ${row.original.Year}`.replace(
                                        new RegExp(searchQuery.trim(), 'gi'), 
                                        match => `<span class="highlight-match">${match}</span>`
                                    )
                                }} />
                            ) : (
                                `${row.original.Quarter} ${row.original.Month} ${row.original.Year}`
                            )}
                        </a>
                    </h4>
                    <div className="quarterly-table-tags">
                        <span className={`quarterly-tag quarterly-tag-${getQuarterColor(row.original.Quarter)}`}>
                            {row.original.Quarter}
                        </span>
                        {isCurrentQuarter(row.original.Year, row.original.Quarter) && (
                            <span className="quarterly-tag quarterly-tag-q1" style={{background: 'var(--ifia-green)'}}>
                                Current
                            </span>
                        )}
                    </div>
                </div>
            ),
            sortFn: 'customSort',
        },
        {
            header: 'Year & Month',
            accessorKey: 'Year',
            cell: ({ row }) => (
                <div className="quarterly-table-meta">
                    <div className="quarterly-meta-item">
                        <i className="fas fa-calendar-alt"></i>
                        <Tippy content={`Year: ${row.original.Year}`}>
                            <span className="quarterly-meta-text">
                                {searchQuery.trim() ? (
                                    <span dangerouslySetInnerHTML={{ 
                                        __html: row.original.Year.replace(
                                            new RegExp(searchQuery.trim(), 'gi'), 
                                            match => `<span class="highlight-match">${match}</span>`
                                        )
                                    }} />
                                ) : (
                                    row.original.Year
                                )}
                            </span>
                        </Tippy>
                    </div>
                    {row.original.Month && (
                        <div className="quarterly-meta-item">
                            <i className="fas fa-calendar"></i>
                            <Tippy content={`Month: ${row.original.Month}`}>
                                <span className="quarterly-meta-text">
                                    {searchQuery.trim() ? (
                                        <span dangerouslySetInnerHTML={{ 
                                            __html: row.original.Month.replace(
                                                new RegExp(searchQuery.trim(), 'gi'), 
                                                match => `<span class="highlight-match">${match}</span>`
                                            )
                                        }} />
                                    ) : (
                                        row.original.Month
                                    )}
                                </span>
                            </Tippy>
                        </div>
                    )}
                </div>
            ),
        },
        {
            header: 'Published Date',
            accessorKey: 'PublishedDate',
            cell: ({ row }) => (
                <div className="quarterly-table-date">
                    <i className="fas fa-calendar-check"></i>
                    <span>
                        {row.original.PublishedDate ? (
                            <Tippy content={`Published: ${formatDateSimple(row.original.PublishedDate)}`}>
                                <span>{formatDate(row.original.PublishedDate)}</span>
                            </Tippy>
                        ) : (
                            <span className="quarterly-status-display archived">
                                No Date Available
                            </span>
                        )}
                    </span>
                </div>
            ),
        },
        {
            header: 'Actions',
            accessorKey: 'actions',
            enableSorting: false,
            cell: ({ row }) => (
                <div className="quarterly-table-actions">
                    <button 
                        className="quarterly-read-more-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            openPDFModal(
                                row.original.id,
                                `${row.original.Quarter} ${row.original.Month} ${row.original.Year}`
                            );
                        }}
                    >
                        <span>View</span>
                        <i className="fas fa-external-link-alt"></i>
                    </button>
                </div>
            )
        }
    ], [searchQuery]);

    /**
     * React Table hooks for managing table behavior
     * 
     * Configures the table with sorting capabilities, initial sort state, and data binding.
     * Sets up descending sort by Year and Quarter by default, and disables sort removal
     * to maintain consistent sorting behavior.
     */
   const table = useTable({
    features,
    data,
    columns,
    enableSortingRemoval: false,
    initialState: {
        sorting: [
        { id: 'Year', desc: true },
        { id: 'Quarter', desc: false },
        ],
    },
    });

    if (!data || data.length === 0) {
        return (
            <div className="quarterly-update-table">
                <div className="no-results">
                    <div className="no-results-content">
                        <i className="fas fa-search"></i>
                        <h3>No quarterly updates found</h3>
                        <p>Try adjusting your search criteria or filters to see more results.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="quarterly-update-table">
            <div className="quarterly-table-container">
                <table className="qtr-update-tbl-custom">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                            const column = header.column;
                            const isSorted = column.getIsSorted(); // 'asc' | 'desc' | false
                            const canSort = column.getCanSort();

                            return (
                                <th
                                key={header.id}
                                colSpan={header.colSpan}
                                className={`quarterly-col-${column.id.toLowerCase().replace(/[^a-z0-9]/g, '')} ${isSorted ? 'sorted' : ''}`}
                                data-sortable={canSort}
                                data-sorted={!!isSorted}
                                aria-sort={
                                    isSorted === 'desc'
                                    ? 'descending'
                                    : isSorted === 'asc'
                                    ? 'ascending'
                                    : 'none'
                                }
                                onClick={canSort ? column.getToggleSortingHandler() : undefined}
                                style={{ cursor: canSort ? 'pointer' : 'default' }}
                                >
                                <div className="quarterly-header-content">
                                    <span className="quarterly-header-text">
                                    {flexRender(column.columnDef.header, header.getContext())}
                                    </span>
                                    {canSort ? (
                                    <span className={`quarterly-sort-icon ${isSorted ? 'active' : ''}`}>
                                        {isSorted === 'desc' ? (
                                        <FontAwesomeIcon icon={faSortDown} title="Sorted Descending" />
                                        ) : isSorted === 'asc' ? (
                                        <FontAwesomeIcon icon={faSortUp} title="Sorted Ascending" />
                                        ) : (
                                        <FontAwesomeIcon icon={faSort} title="Click to Sort" />
                                        )}
                                    </span>
                                    ) : (
                                    <span className="quarterly-sort-icon disabled" style={{ opacity: 0.3 }}>
                                        <FontAwesomeIcon icon={faGripVertical} />
                                    </span>
                                    )}
                                </div>
                                </th>
                            );
                            })}
                        </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="quarterly-table-row">
                            {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                            ))}
                        </tr>
                        ))}
                    </tbody>
                    </table>
            </div>

            {modals.map((modal, index) => (
                <PDFModal
                    key={`${modal.id}-${index}`}
                    title={modal.title}
                    show={modal.isOpen} 
                    onClose={e => {
                        e.stopPropagation(); 
                        closePDFModal(modal.id);
                    }}
                    allowFullscreen={true}
                    allowMove={true}
                    showLinkinToolBar={true}
                    listItemId={modal.id}
                />
            ))}
        </div>
    );
}

export default QuarterlyUpdateTable;
